import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Loader2, Send } from 'lucide-react';
import Button from '../ui/Button';
import { ChipGroup, Input, Select, Textarea } from '../ui/Field';
import { useToast } from '../ui/Toast';
import usePickupRequests from '../../hooks/usePickupRequests';
import { quantityUnits, wasteCategories } from '../../data/wasteCategories';
import { todayISO } from '../../lib/format';
import {
  email as emailRule,
  minLength,
  notPastDate,
  phone as phoneRule,
  positiveNumber,
  required,
  validate,
} from '../../lib/validators';

const EMPTY_FORM = {
  company: '',
  contactPerson: '',
  email: '',
  phone: '',
  address: '',
  categories: [],
  quantity: '',
  unit: 'kg',
  pickupDate: '',
  notes: '',
};

/** Validation schema — the single place field rules are declared. */
const SCHEMA = {
  company: [required('Company name'), minLength('Company name', 2)],
  contactPerson: [required('Contact person'), minLength('Contact person', 2)],
  email: [required('Email'), emailRule],
  phone: [required('Phone number'), phoneRule],
  address: [required('Pickup address'), minLength('Pickup address', 10)],
  categories: [required('At least one e-waste category')],
  quantity: [required('Approximate quantity'), positiveNumber('Quantity')],
  unit: [required('Unit')],
  pickupDate: [required('Preferred pickup date'), notPastDate('Pickup date')],
};

export default function PickupRequestForm() {
  const { addRequest } = usePickupRequests();
  const toast = useToast();

  const [values, setValues] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const setField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    // Clear the error as soon as the field becomes valid again.
    if (touched[field]) {
      const fieldErrors = validate({ ...values, [field]: value }, { [field]: SCHEMA[field] || [] });
      setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] || '' }));
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const fieldErrors = validate(values, { [field]: SCHEMA[field] || [] });
    setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] || '' }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextErrors = validate(values, SCHEMA);
    setErrors(nextErrors);
    setTouched(Object.keys(SCHEMA).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    if (Object.keys(nextErrors).length > 0) {
      toast({
        variant: 'error',
        title: 'Please check the highlighted fields',
        description: `${Object.keys(nextErrors).length} field(s) need attention before we can submit.`,
      });
      // Move focus to the first invalid control for keyboard and screen-reader users.
      document.querySelector('[aria-invalid="true"]')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }

    setSubmitting(true);
    // Brief delay so the demo reads like a real network round-trip.
    setTimeout(() => {
      const record = addRequest({ ...values, quantity: Number(values.quantity) });
      setSubmitted(record);
      setSubmitting(false);
      setValues(EMPTY_FORM);
      setErrors({});
      setTouched({});
      toast({
        title: 'Pickup request submitted',
        description: `Reference ${record.id}. Our team responds within 24 hours.`,
      });
    }, 700);
  };

  if (submitted) {
    return <SuccessPanel request={submitted} onReset={() => setSubmitted(null)} />;
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="rounded-xl2 border border-ink-100 bg-white p-6 shadow-soft sm:p-8">
      <h2 className="font-display text-xl font-semibold text-ink-900">Pickup & quotation request</h2>
      <p className="mt-2 text-sm text-ink-500">
        Fields marked <span className="text-brand-600">*</span> are required. We respond with a
        quotation within 24 hours.
      </p>

      <div className="mt-8 space-y-6">
        <div className="grid gap-6 sm:grid-cols-2">
          <Input
            label="Company Name"
            required
            placeholder="Acme Industries Pvt. Ltd."
            value={values.company}
            onChange={(e) => setField('company', e.target.value)}
            onBlur={() => handleBlur('company')}
            error={touched.company ? errors.company : ''}
          />
          <Input
            label="Contact Person"
            required
            placeholder="Full name"
            value={values.contactPerson}
            onChange={(e) => setField('contactPerson', e.target.value)}
            onBlur={() => handleBlur('contactPerson')}
            error={touched.contactPerson ? errors.contactPerson : ''}
          />
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <Input
            label="Work Email"
            type="email"
            required
            placeholder="name@company.com"
            value={values.email}
            onChange={(e) => setField('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            error={touched.email ? errors.email : ''}
          />
          <Input
            label="Phone Number"
            type="tel"
            required
            placeholder="+91 98200 00000"
            value={values.phone}
            onChange={(e) => setField('phone', e.target.value)}
            onBlur={() => handleBlur('phone')}
            error={touched.phone ? errors.phone : ''}
          />
        </div>

        <Textarea
          label="Pickup Address"
          required
          rows={3}
          placeholder="Building, street, area, city, state and PIN code"
          value={values.address}
          onChange={(e) => setField('address', e.target.value)}
          onBlur={() => handleBlur('address')}
          error={touched.address ? errors.address : ''}
        />

        <ChipGroup
          label="E-Waste Category"
          required
          options={wasteCategories}
          value={values.categories}
          onChange={(next) => {
            setValues((prev) => ({ ...prev, categories: next }));
            if (touched.categories) {
              setErrors((prev) => ({
                ...prev,
                categories: next.length ? '' : 'At least one e-waste category is required',
              }));
            }
          }}
          hint="Select every category present in the lot — you can pick more than one."
          error={touched.categories ? errors.categories : ''}
        />

        <div className="grid gap-6 sm:grid-cols-3">
          <Input
            label="Approximate Quantity"
            type="number"
            min="0"
            step="any"
            required
            placeholder="1500"
            value={values.quantity}
            onChange={(e) => setField('quantity', e.target.value)}
            onBlur={() => handleBlur('quantity')}
            error={touched.quantity ? errors.quantity : ''}
          />
          <Select
            label="Unit"
            required
            options={quantityUnits}
            value={values.unit}
            onChange={(e) => setField('unit', e.target.value)}
            onBlur={() => handleBlur('unit')}
            error={touched.unit ? errors.unit : ''}
          />
          <Input
            label="Preferred Pickup Date"
            type="date"
            required
            min={todayISO()}
            value={values.pickupDate}
            onChange={(e) => setField('pickupDate', e.target.value)}
            onBlur={() => handleBlur('pickupDate')}
            error={touched.pickupDate ? errors.pickupDate : ''}
          />
        </div>

        <Textarea
          label="Additional Notes"
          rows={4}
          placeholder="Asset counts, data destruction requirements, site access constraints, entity name for documentation…"
          value={values.notes}
          onChange={(e) => setField('notes', e.target.value)}
          hint="Optional, but the more detail you give, the more accurate the quotation."
        />
      </div>

      <div className="mt-8 flex flex-col gap-4 border-t border-ink-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
       
        <Button type="submit" size="lg" disabled={submitting} className="flex-none">
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Submit Request
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

/** Confirmation shown in place of the form after a successful submission. */
function SuccessPanel({ request, onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-xl2 border border-brand-200 bg-white p-8 text-center shadow-soft sm:p-12"
      role="status"
    >
      <motion.span
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.12, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-brand-50 text-brand-600"
      >
        <CheckCircle2 className="h-9 w-9" strokeWidth={2} />
      </motion.span>

      <h2 className="mt-6 font-display text-2xl font-bold text-ink-900">Request received</h2>
      <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink-500">
        Thank you, {request.contactPerson}. Our team will verify the inventory and come back with a
        quotation within 24 working hours.
      </p>

      <div className="mx-auto mt-7 inline-flex flex-col items-center rounded-xl2 border border-ink-100 bg-surface px-8 py-5">
        <span className="text-xs font-medium uppercase tracking-wider text-ink-500">
          Your reference number
        </span>
        <span className="mt-1.5 font-display text-2xl font-bold tracking-tight text-brand-700">
          {request.id}
        </span>
      </div>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Button to="/" size="lg">
          Back to Home
          <ArrowRight className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="lg" onClick={onReset}>
          Submit another request
        </Button>
      </div>

      <p className="mt-6 text-xs text-ink-400">
        Need to add something? Reply to the confirmation with your reference number, or{' '}
        <Link to="/contact" className="font-medium text-brand-700 underline underline-offset-2">
          contact the team
        </Link>
        .
      </p>
    </motion.div>
  );
}
